import type { Annotation, Friend, FriendStatus, SharedAnnotation } from './types.ts'
import type { StorageLike } from './annotations.ts'
import type { RemoteAnnotations } from './sync.ts'

/**
 * The Firebase adapter.
 *
 * Everything here is loaded dynamically. The SDK is a large dependency and the app
 * is designed to work without it entirely, so it must not sit in the initial bundle
 * that a user on a festival network downloads before they can read the programme.
 */

export interface FirebaseWebConfig {
  projectId: string
  apiKey: string
  appId: string
  authDomain?: string
  messagingSenderId?: string
}

export interface RuntimeConfig {
  firebase?: FirebaseWebConfig
  vapidPublicKey?: string
}

/** The last configuration the server served, for the loads that cannot reach it. */
export const CONFIG_CACHE_KEY = 'oya-annotated:config:v1'

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    // Accessing localStorage can itself throw when cookies are blocked.
    return null
  }
}

/** The cached configuration, or null when there is nothing usable to fall back to. */
function cachedConfig(storage: StorageLike | null): RuntimeConfig | null {
  try {
    const raw = storage?.getItem(CONFIG_CACHE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    // Anything that is not a plain object is a corrupt or stale-schema copy. Discarded
    // rather than returned: `parsed.firebase` on a string would throw at the call site.
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null
    return parsed as RuntimeConfig
  } catch {
    return null
  }
}

/**
 * Reads GET /config.json. An empty object means "stay local-only".
 *
 * The response is cached, and the cached copy is served whenever the request fails.
 * Without it a cold start with no signal drops the cloud layer for the entire session —
 * no project id means no sign-in and no sync until the app is next opened *and* the
 * request happens to succeed. The values are public by construction (they ship in every
 * page load), so there is nothing here that must not sit in localStorage (KTD13).
 */
export async function loadRuntimeConfig({
  fetchImpl = fetch,
  storage = defaultStorage(),
}: { fetchImpl?: typeof fetch; storage?: StorageLike | null } = {}): Promise<RuntimeConfig> {
  try {
    // A stalled connection must not leave the app stuck at "not ready" forever.
    const response = await fetchImpl('/config.json', {
      cache: 'no-store',
      signal: AbortSignal.timeout(5000),
    })
    // A 5xx is a failed request like any other, so it falls back rather than dropping
    // to local-only. Only a 200 is an answer, including the `{}` that means no project
    // is configured — which is cached too, so an offline start does not resurrect a
    // configuration the deployment no longer has.
    if (!response.ok) return cachedConfig(storage) ?? {}
    const config = (await response.json()) as RuntimeConfig
    try {
      storage?.setItem(CONFIG_CACHE_KEY, JSON.stringify(config))
    } catch {
      // A full or blocked store costs the fallback, nothing else.
    }
    return config
  } catch {
    // Offline, or the endpoint is missing. Local-only is the right last resort: the
    // programme and existing notes still work.
    return cachedConfig(storage) ?? {}
  }
}

export interface FirebaseBundle {
  auth: import('firebase/auth').Auth
  db: import('firebase/firestore').Firestore
  /** Everything the sync layer drives, the friend-list listener included. */
  remote: RemoteAnnotations
  signIn(): Promise<void>
  signOut(): Promise<void>
  getIdToken(): Promise<string | null>
}

const COLLECTION = 'annotations'
const FRIEND_LISTS = 'friendLists'

/** `annotations/{uid}_{concertId}` — the deterministic id from KTD6. */
export function annotationDocId(uid: string, concertId: string): string {
  return `${uid}_${concertId}`
}

export function toAnnotation(data: Record<string, unknown>): Annotation {
  return {
    concertId: String(data['concertId'] ?? ''),
    uid: (data['uid'] as string | null) ?? null,
    ownerName: (data['ownerName'] as string | null) ?? null,
    vote: (data['vote'] as Annotation['vote']) ?? null,
    note: String(data['note'] ?? ''),
    // Hand-written mapper: a field missing here is dropped on every read, and a
    // document written before terningkast existed simply has no `dice` key.
    dice: typeof data['dice'] === 'number' ? (data['dice'] as number) : null,
    updatedAt: String(data['updatedAt'] ?? ''),
    sharedWith: Array.isArray(data['sharedWith']) ? (data['sharedWith'] as string[]) : [],
    // `readers` is deliberately absent: it is a grant, and the only version of it this
    // client may act on is the one it composes from the live friend list at write time.
    // Reading it back would let a document written before a friend was removed carry
    // that friend into the next upload.
  }
}

/**
 * The friend feed's rows, out of one `readers array-contains me` snapshot.
 *
 * The reader's own annotations match that query by construction — `readers` always
 * carries the owner (KTD3) — so dropping them is load-bearing here, unlike the
 * identical-looking guard in `subscribeShared`, where they cannot match at all. Without
 * it every note the user wrote would come back down the friend feed and be rendered on
 * their own schedule as somebody else's.
 *
 * A row with no `ownerName` is kept. Google does not promise a display name, and the
 * name shown in front of a friend's note comes from the friend list anyway (R14, KTD6);
 * dropping the row would hide a real note to avoid printing a fallback.
 *
 * Exported for the test beside the layer it feeds — this filter is the whole difference
 * between a friend feed and a mirror.
 */
export function visibleAnnotations(list: readonly Annotation[], uid: string): SharedAnnotation[] {
  return list.filter((annotation) => annotation.uid !== uid)
}

function isFriendStatus(value: unknown): value is FriendStatus {
  return value === 'incoming' || value === 'outgoing' || value === 'accepted'
}

/**
 * Turns a `friendLists/{uid}` document into the store's list.
 *
 * The document holds two fields that are deliberately not redundant: `uids` (accepted
 * friends only, which the rules bound the client's `readers` writes by) and this
 * `friends` map, which also carries requests either way. The client reads the map,
 * because the friends page has to show a request; the *status* is what decides whether
 * an entry ever reaches `readers`, so an unrecognised one is dropped rather than
 * guessed at (KTD4).
 *
 * Exported for the test beside the store it feeds — this mapper is where a Firestore
 * document becomes a consent decision, and `friends.test.ts` asserts it fails closed.
 */
export function toFriends(data: Record<string, unknown> | undefined): Friend[] {
  const map = data?.['friends']
  if (typeof map !== 'object' || map === null) return []

  const friends: Friend[] = []
  for (const [uid, value] of Object.entries(map as Record<string, unknown>)) {
    if (typeof value !== 'object' || value === null) continue
    const entry = value as Record<string, unknown>
    if (!isFriendStatus(entry['status'])) continue
    friends.push({
      uid,
      // Both are absent on an invitation held for someone who has never signed in,
      // and the name prefix has a fallback for exactly that (R9, R14).
      email: typeof entry['email'] === 'string' ? entry['email'] : null,
      name: typeof entry['name'] === 'string' ? entry['name'] : null,
      status: entry['status'],
    })
  }
  return friends
}

export async function createFirebase(config: FirebaseWebConfig): Promise<FirebaseBundle> {
  const { initializeApp, getApps } = await import('firebase/app')
  const {
    getAuth,
    GoogleAuthProvider,
    signInWithRedirect,
    getRedirectResult,
    signOut: fbSignOut,
  } = await import('firebase/auth')
  const {
    initializeFirestore,
    persistentLocalCache,
    persistentMultipleTabManager,
    collection,
    doc,
    setDoc,
    deleteDoc,
    getDocs,
    onSnapshot,
    query,
    where,
  } = await import('firebase/firestore')

  const app =
    getApps()[0] ??
    initializeApp({
      apiKey: config.apiKey,
      appId: config.appId,
      projectId: config.projectId,
      // Our own host. signInWithRedirect against <project>.firebaseapp.com is
      // blocked by Safari's storage partitioning, and signInWithPopup does not
      // work inside an installed iOS PWA at all — which is exactly where install
      // is mandatory for push. See KTD5.
      authDomain: config.authDomain ?? window.location.host,
      ...(config.messagingSenderId ? { messagingSenderId: config.messagingSenderId } : {}),
    })

  const auth = getAuth(app)
  const db = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  })

  // Resolve a pending redirect sign-in, if this load is the return leg.
  await getRedirectResult(auth).catch(() => null)

  const remote: RemoteAnnotations = {
    async upsert(annotation, readers) {
      if (!annotation.uid) return
      const id = annotationDocId(annotation.uid, annotation.concertId)
      // sharedWith is deliberately not written from the client: the rules reject it
      // and only the server may grant a share.
      //
      // `readers` is the opposite case — written on every upload, and taken from the
      // caller rather than from the record. Whatever rode along on the in-memory copy is
      // dropped here so a stale array can never be republished; the rules bound what
      // lands to the caller's own `friendLists/{uid}.uids` (KTD3).
      const { sharedWith: _ignored, readers: _stale, ...writable } = annotation
      void _ignored
      void _stale
      await setDoc(
        doc(db, COLLECTION, id),
        { ...writable, readers: [...readers] },
        { merge: true },
      )
    },

    subscribeOwn(uid, onChange) {
      const q = query(collection(db, COLLECTION), where('uid', '==', uid))
      return onSnapshot(
        q,
        (snapshot) =>
          onChange(
            snapshot.docs.map((d) => ({
              annotation: toAnnotation(d.data()),
              // Read only to stamp what the cloud holds — never merged back into the
              // record, so a stale grant can still not be republished.
              readers: Array.isArray(d.data()['readers'])
                ? (d.data()['readers'] as string[])
                : [],
            })),
          ),
        // Report the error, but do not push an empty list: Firestore does not
        // resubscribe after a listener error, so "[]" would permanently erase the
        // user's own annotations from the view rather than just stopping updates.
        (error) => console.warn('Firestore-lytter feilet', error),
      )
    },

    subscribeShared(uid, onChange) {
      const q = query(collection(db, COLLECTION), where('sharedWith', 'array-contains', uid))
      return onSnapshot(
        q,
        (snapshot) =>
          onChange(
            snapshot.docs
              .map((d) => toAnnotation(d.data()))
              // Our own rows can match neither clause, but guard anyway.
              .filter((a) => a.uid !== uid),
          ),
        // Same reasoning: an error must not wipe the notes friends have shared.
        (error) => console.warn('Firestore-lytter feilet', error),
      )
    },

    subscribeVisible(uid, onChange) {
      const q = query(collection(db, COLLECTION), where('readers', 'array-contains', uid))
      return onSnapshot(
        q,
        (snapshot) =>
          onChange(visibleAnnotations(snapshot.docs.map((d) => toAnnotation(d.data())), uid)),
        // Same reasoning again, and it bites hardest here: this listener carries every
        // friend's whole schedule, so "[]" after an error would empty the day view of
        // everyone else's notes with no way back short of a reload.
        (error) => console.warn('Firestore-lytter feilet', error),
      )
    },

    subscribeFriendList(uid, onChange) {
      // A single-document listener rather than a query: the whole model is one document
      // per person (KTD4), so there is nothing to query for, and a document read is the
      // cheapest thing Firestore offers. It is served from `persistentLocalCache` while
      // offline, which is why the friend list survives a dead network at all.
      return onSnapshot(
        doc(db, FRIEND_LISTS, uid),
        // A user with no friends has no document, and `data()` is undefined there.
        // That is a real answer — "nobody" — and pushing an empty list for it is
        // correct, unlike the error path below.
        (snapshot) => onChange(toFriends(snapshot.data())),
        // Report the error, but do not push an empty list: Firestore does not
        // resubscribe after a listener error, so "[]" would permanently erase the
        // friend list from view — and with it every friend's name prefix — rather
        // than just stopping updates.
        (error) => console.warn('Firestore-lytter feilet', error),
      )
    },

    async remove(uid, concertId) {
      await deleteDoc(doc(db, COLLECTION, annotationDocId(uid, concertId)))
    },

    async deleteAllOwnedBy(uid) {
      const q = query(collection(db, COLLECTION), where('uid', '==', uid))
      const snapshot = await getDocs(q)
      await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)))
    },
  }

  return {
    auth,
    db,
    remote,

    async signIn() {
      const provider = new GoogleAuthProvider()
      provider.setCustomParameters({ prompt: 'select_account' })
      await signInWithRedirect(auth, provider)
    },
    async signOut() {
      await fbSignOut(auth)
    },
    async getIdToken() {
      return auth.currentUser ? auth.currentUser.getIdToken() : null
    },
  }
}
